import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import styled from 'styled-components';
import { UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { canAccessRoute } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import { DOCUMENTATION_PATH, isDocumentationRole } from '../utils/roleLanding';

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f0f2f5;
  gap: 16px;
`;

const LoadingText = styled.div`
  color: #666;
  font-size: 16px;
`;

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [UserRole.SUPER_ADMIN, UserRole.AGENCY_ADMIN],
  redirectTo = '/login'
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // Show loading spinner while authentication state is being determined
  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
        <LoadingText>{t('common.loading')}</LoadingText>
      </LoadingContainer>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check if user's role is allowed to access this route
  // Dokumentations-Benutzer (DGKP, 24h-Betreuer, Angehoerige) kennen nur das
  // Doku-Modul — alte Links/Lesezeichen (z. B. /dashboard) fuehren sie dorthin
  // statt auf "Zugriff verweigert".
  if (
    isDocumentationRole(user.role, user.permissions) &&
    !location.pathname.startsWith(DOCUMENTATION_PATH) &&
    (!allowedRoles.includes(user.role) || !canAccessRoute(user, location.pathname))
  ) {
    return <Navigate to={DOCUMENTATION_PATH} replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    console.warn(`🚫 Role ${user.role} not allowed for route ${location.pathname}. Allowed roles:`, allowedRoles);

    // Show access denied page
    return <AccessDenied message={t('errors.roleNotAllowed')} />;
  }

  // Check if user has permission to access this specific route
  if (!canAccessRoute(user, location.pathname)) {
    console.warn(`🚫 Access denied to ${location.pathname} for user:`, user.email);
    console.warn(`👤 User permissions:`, user.permissions);

    // Show access denied page
    return <AccessDenied message={t('errors.noPermissionForPage')} />;
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute; 